import React from 'react';
import type { AgiBackTaskInfo } from '../../api/agiBackTask';
import type { TaskDetailMaterialDisplay } from '../../utils/taskDetailMaterialResolve';
import { PromptTextHighlight } from '../../components/PromptTextHighlight';
import './taskDetail.css';

const roleLabel: Record<string, string> = {
  reference_image: '参考图',
  reference_video: '参考视频',
  reference_audio: '参考音频',
};

const typeLabel: Record<string, string> = {
  image: '图片',
  video: '视频',
  audio: '音频',
};

export type TaskDetailViewProps = {
  loading: boolean;
  error: string | null;
  detail: AgiBackTaskInfo | null;
  queryTaskId?: string;
  materialsLoading?: boolean;
  materials?: TaskDetailMaterialDisplay[];
  headerExtra?: React.ReactNode;
};

export const TaskDetailView: React.FC<TaskDetailViewProps> = ({
  loading,
  error,
  detail,
  queryTaskId,
  materialsLoading = false,
  materials = [],
  headerExtra,
}) => {
  const prompts = [...(detail?.prompts ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  );

  if (loading) {
    return (
      <div className="task-detail-page-body task-detail-page-body--state">
        <div className="task-detail-loading">
          <div className="spinner" aria-hidden />
          <span>加载中…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="task-detail-page-body task-detail-page-body--state">
        <p className="task-detail-error">{error}</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="task-detail-page-body task-detail-page-body--state">
        <p className="task-detail-empty">暂无数据</p>
      </div>
    );
  }

  const remoteId = detail.remoteTaskId || queryTaskId;

  return (
    <div className="task-detail-page-body">
      {headerExtra}

      <div className="task-detail-card task-detail-card--hero">
        {remoteId ? (
          <div className="task-detail-id-block">
            <span className="task-detail-id-label">任务 ID</span>
            <code className="task-detail-id-value" title={remoteId}>
              {remoteId}
            </code>
          </div>
        ) : null}
        <div className="task-detail-chip-row" aria-label="任务参数">
          {detail.model ? <span className="task-detail-chip">{detail.model}</span> : null}
          {detail.resolution ? <span className="task-detail-chip">{detail.resolution}</span> : null}
          {detail.aspectRatio ? <span className="task-detail-chip">比例 {detail.aspectRatio}</span> : null}
          {detail.durationSec != null ? (
            <span className="task-detail-chip">时长 {detail.durationSec}s</span>
          ) : null}
        </div>
      </div>

      <section className="task-detail-section task-detail-section--prompt">
      
        {prompts.length ? (
          <div className="task-detail-prompt-prose">
            {prompts.map((p, idx) => (
              <article key={p.id ?? idx} className="task-detail-prompt-block">
                {prompts.length > 1 ? (
                  <div className="task-detail-prompt-seg">第 {(p.sortOrder ?? idx) + 1} 段</div>
                ) : null}
                <p className="task-detail-prompt-text">
                  <PromptTextHighlight text={p.content?.trim() || '（空）'} />
                </p>
              </article>
            ))}
          </div>
        ) : (
          <p className="task-detail-empty">暂无提示词记录</p>
        )}
      </section>

      <section className="task-detail-section task-detail-section--materials">
        {materialsLoading ? (
          <div className="task-detail-loading task-detail-loading--inline">
            <div className="spinner" aria-hidden />
            <span>正在按素材 ID 拉取预览…</span>
          </div>
        ) : materials.length ? (
          <div className="task-detail-materials">
            {materials.map((m) => {
              const preview = (m.thumb || m.url || '').trim();
              const role = m.role ? roleLabel[m.role] ?? m.role : null;
              const kind = typeLabel[m.type] ?? m.type;
              return (
                <div key={m.assetId} className="task-detail-material-card">
                  <div className="task-detail-material-preview">
                    {preview && m.type === 'video' ? (
                      <video src={preview} muted playsInline preload="metadata" />
                    ) : preview && m.type === 'audio' ? (
                      <div className="task-detail-material-audio" aria-hidden>
                        🎵
                      </div>
                    ) : preview ? (
                      <img src={preview} alt={m.name} loading="lazy" />
                    ) : (
                      <div className="task-detail-material-placeholder">无预览</div>
                    )}
                  </div>
                  <div className="task-detail-material-info">
                    <div className="name" title={m.name}>
                      {m.name}
                    </div>
                    <div className="meta">
                      {role ? `${role} · ` : ''}
                      {kind}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="task-detail-empty">暂无关联素材</p>
        )}
      </section>
    </div>
  );
};

export default TaskDetailView;
